import type { AxiosResponse } from 'axios';

import {
  MakeResponse,
  ModelResponse,
  ISearch,
  ISearchResponse,
  RetrieveSuggestedSectionResponse,
} from '../api/types/searchApiTypes';
import {
  donedealGateway,
  donedealServicesGateway,
} from '../api/api-utils/commonApiUtils';
import { API_CLIENT_TIMEOUT } from '../utils';
import { z } from 'zod';

/*
 * @deprecated - This api is deprecated in favour of the new search filters api
 * https://distilledsch.tpondemand.com/entity/95201-migrate-from-legacy-homepage-filters-to
 * */
async function getAllMakes(): Promise<AxiosResponse<MakeResponse>> {
  const response: AxiosResponse<MakeResponse> = await donedealGateway.get(
    `/ddapi/legacy/search/api/v3/sections/filters/search/cars`,
  );
  return response;
}

/*
 * @deprecated - This api is deprecated in favour of the new search filters api
 * https://distilledsch.tpondemand.com/entity/95201-migrate-from-legacy-homepage-filters-to
 * */
async function getAllModels(
  make: string,
): Promise<AxiosResponse<ModelResponse>> {
  const response: AxiosResponse<ModelResponse> = await donedealGateway.get(
    `/ddapi/legacy/search/api/v3/sections/filters/search/cars/make/${make}/values`,
  );
  return response;
}

/**
 * @deprecated - This api is deprecated, use getCarsCount instead
 */
async function getMotorCount(
  payload: ISearch,
): Promise<AxiosResponse<ISearchResponse>> {
  const response: AxiosResponse<ISearchResponse> = await donedealGateway.post(
    `/ddapi/legacy/search/api/v4/find/`,
    payload,
    {
      timeout: API_CLIENT_TIMEOUT,
    },
  );
  return response;
}

const getCarsCountSchema = z.object({
  paging: z.object({
    totalResults: z.number(),
  }),
});

type GetCarsCountPayload = {
  ranges: {
    from: string | undefined;
    to: string | undefined;
    name: string;
  }[];
  paging: {
    from: number;
    pageSize: number;
  };
  makeModelFilters?: {
    make: string;
    model: string | undefined;
    trim: string | undefined;
  }[];
  sections: string[];
};

async function getCarsCount({
  make,
  model,
  priceFrom,
  priceTo,
  yearFrom,
  yearTo,
}: {
  make: string | undefined;
  model: string | undefined;
  priceFrom: string | undefined;
  priceTo: string | undefined;
  yearFrom: string | undefined;
  yearTo: string | undefined;
}): Promise<number> {
  const payload: GetCarsCountPayload = {
    ranges: [
      ...(priceFrom || priceTo
        ? [
            {
              from: priceFrom,
              to: priceTo,
              name: 'price',
            },
          ]
        : []),
      ...(yearFrom || yearTo
        ? [
            {
              from: yearFrom,
              to: yearTo,
              name: 'year',
            },
          ]
        : []),
    ],
    sections: ['cars'],
    paging: { from: 0, pageSize: 0 },
  };

  if (make) {
    payload.makeModelFilters = [
      {
        make,
        model,
        trim: undefined,
      },
    ];
  }

  const response: AxiosResponse = await donedealServicesGateway.post(
    `/ddapi/v2/search`,
    payload,
  );

  const data = getCarsCountSchema.parse(response.data);

  return data.paging.totalResults;
}

async function getSuggestedSections(
  title: string,
): Promise<AxiosResponse<RetrieveSuggestedSectionResponse[]>> {
  const response: AxiosResponse<RetrieveSuggestedSectionResponse[]> =
    await donedealGateway.post(`/ddapi/v1/search/suggested/sections`, {
      title,
    });
  return response;
}

export const searchApi = {
  getAllMakes,
  getAllModels,
  getMotorCount,
  getCarsCount,
  getSuggestedSections,
};
